import {useEffect, useRef} from 'react';

const IDLE_TIMEOUT_MINUTES = 20;
const HEARTBEAT_INTERVAL_MINUTES = 5;
const UTM_EXPIRY_MINUTES = 20;

function getOrResetSessionIdAndUtmParams() {
    const now = new Date();
    const injectedHeaders = typeof window.__HEADERS__ === 'object' ? window.__HEADERS__ : {};
    const searchParams = new URLSearchParams(window.location.search);

    // UTM expiry logic
    const savedAt = sessionStorage.getItem("utmSavedAt");
    const isExpired = savedAt && (now - new Date(savedAt)) > UTM_EXPIRY_MINUTES * 60 * 1000;
    if (isExpired) {
        sessionStorage.removeItem("utmParams");
        sessionStorage.removeItem("utmSavedAt");
    }

    // Prefer headers, fallback to URL, then to ''
    const utmParams = {
        utm_source: injectedHeaders.utmSource || searchParams.get("utm_source") || '',
        utm_medium: injectedHeaders.utmMedium || searchParams.get("utm_medium") || '',
        utm_campaign: injectedHeaders.utmCampaign || searchParams.get("utm_campaign") || '',
        utm_content: injectedHeaders.utmContent || searchParams.get("utm_content") || '',
        utm_term: injectedHeaders.utmTerm || searchParams.get("utm_term") || ''
    };

    const oktaId =
        injectedHeaders.oktaId ||
        searchParams.get("x-okta-id") ||
        sessionStorage.getItem("oktaId") ||
        '';

    let sessionId = sessionStorage.getItem("session_id");
    if (!sessionId) {
        sessionId = crypto.randomUUID();
        sessionStorage.setItem("session_id", sessionId);
    }

    // Save to sessionStorage
    sessionStorage.setItem("utmParams", JSON.stringify(utmParams));
    sessionStorage.setItem("oktaId", oktaId);
    sessionStorage.setItem("utmSavedAt", now.toString());

    return {sessionId, utmParams, oktaId};
}

const SessionManagement = ({organisation, destinationGroup, _restServicePost}) => {
    const sessionId = useRef(null);
    const utmParams = useRef(null);
    const oktaId = useRef(null);
    const sessionStartTime = useRef(new Date().toISOString());
    const sessionEnded = useRef(false);
    const idleTimeout = useRef(null);
    const heartbeatInterval = useRef(null);

    const sendSessionEnd = (reason = "unload") => {
        if (sessionEnded.current) return;
        sessionEnded.current = true;

        const payload = {
            session_id: sessionId.current,
            organisation_id: organisation.id,
            destination_group_id: destinationGroup.id,
            session_end: new Date().toISOString(),
            end_reason: reason
        };

        navigator.sendBeacon(
            process.env.REACT_APP_API_BASE_URL + 'addSessionInformation',
            new Blob([JSON.stringify(payload)], {type: 'application/json'})
        );
    };

    const sendHeartbeat = () => {
        const payload = {
            session_id: sessionId.current,
            timestamp: new Date().toISOString()
        };
        _restServicePost('sessionHeartbeat', payload)
            .then(() => console.log("Heartbeat sent"))
            .catch((err) => console.error("Heartbeat failed", err));
    };

    const resetIdleTimer = () => {
        clearTimeout(idleTimeout.current);
        idleTimeout.current = setTimeout(() => {
            console.log("⏳ Idle timeout, ending session");
            sendSessionEnd("idle_timeout");
        }, IDLE_TIMEOUT_MINUTES * 60 * 1000);
    };

    useEffect(() => {
        if (!organisation?.id || !destinationGroup?.id) return;

        // Reset session-ended flag in case of remount
        sessionEnded.current = false;

        // Fetch and store session info
        const sessionInfo = getOrResetSessionIdAndUtmParams();
        sessionId.current = sessionInfo.sessionId;
        utmParams.current = sessionInfo.utmParams;
        oktaId.current = sessionInfo.oktaId;

        console.log("Session ID:", sessionId.current);
        console.log("UTM Params:", utmParams.current);
        console.log("Okta ID:", oktaId.current);

        const payload = {
            session_id: sessionId.current,
            organisation_id: organisation.id,
            destination_group_id: destinationGroup.id,
            club_code: organisation.clubCode || '',
            venue_id: organisation.venueId || null,
            session_start: sessionStartTime.current,
            okta_id: oktaId.current,
            campaign_id: utmParams.current.utm_campaign,
            campaign_source: utmParams.current.utm_source,
            campaign_medium: utmParams.current.utm_medium,
            campaign_name: utmParams.current.utm_content,
            campaign_term: utmParams.current.utm_term
        };

        _restServicePost('addSessionInformation', payload)
            .then(() => console.log("Session start logged:", sessionId.current))
            .catch((error) => console.error("Session logging failed", error));

        // Cleanup handlers
        const unloadHandler = () => sendSessionEnd("unload");
        const visibilityHandler = () => {
            if (document.visibilityState === "hidden") {
                sendSessionEnd("visibility_hidden");
            }
        };

        // Setup listeners
        window.addEventListener("beforeunload", unloadHandler);
        window.addEventListener("unload", unloadHandler);
        document.addEventListener("visibilitychange", visibilityHandler);

        const activityEvents = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
        activityEvents.forEach(e => window.addEventListener(e, resetIdleTimer));
        resetIdleTimer();

        // Heartbeat setup
        sendHeartbeat();
        heartbeatInterval.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MINUTES * 60 * 1000);

        return () => {
            clearTimeout(idleTimeout.current);
            clearInterval(heartbeatInterval.current);
            window.removeEventListener("beforeunload", unloadHandler);
            window.removeEventListener("unload", unloadHandler);
            document.removeEventListener("visibilitychange", visibilityHandler);
            activityEvents.forEach(e => window.removeEventListener(e, resetIdleTimer));
        };
    }, [organisation, destinationGroup, _restServicePost]);

    return null;
};

export default SessionManagement;
